import { useEffect, useRef } from 'react'
import { AppEventsContext } from '../../contexts/AppEventsContext'
import { ConversationContext } from './ConversationContext'
import { createConversationDwellClock } from './conversationDwell'

/**
 * Emits `conversation-dwell` — how long the panel stayed open, per visible stretch (the clock:
 * `conversationDwell.ts`). The ONE place the event fires. Call it once per page, from the host
 * (`ArticleConversation`), next to `useTrackConversationOpened`. Silent while the page has no
 * registered container.
 *
 * The effect is keyed on `isOpen` / `containerId` ONLY: `appEvents` gets a new identity on every
 * provider render and `request` on every `open()`, and either in the deps would split one
 * stretch into pieces (and drop the ones under 1 s). Both are read through refs instead.
 */
export function useTrackConversationDwell(): void {
  const conversation = ConversationContext.useOptionalContainer()
  const appEvents = AppEventsContext.useOptionalContainer()
  const isOpen = conversation?.isOpen ?? false
  const containerId = conversation?.containerId ?? null
  const appEventsRef = useRef(appEvents)
  appEventsRef.current = appEvents
  const sourceRef = useRef(conversation?.request?.source ?? null)
  sourceRef.current = conversation?.request?.source ?? null

  useEffect(() => {
    const source = sourceRef.current
    if (!isOpen || !containerId || !source) {
      return undefined
    }
    const clock = createConversationDwellClock({
      source,
      containerId,
      visibleAtOpen: document.visibilityState === 'visible',
      now: () => performance.now(),
      emit: stretch => appEventsRef.current?.appEvent({ event: 'conversation-dwell', ...stretch }),
    })
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        clock.visible()
      } else {
        clock.hidden()
      }
    }
    const onPageHide = (): void => clock.hidden()
    // A page restored from the back/forward cache with the panel open starts a new stretch
    // (idempotent with `visibilitychange` → visible, which may also fire).
    const onPageShow = (e: PageTransitionEvent): void => {
      if (e.persisted) {
        clock.visible()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', onPageHide)
    window.addEventListener('pageshow', onPageShow)
    return () => {
      clock.end()
      window.removeEventListener('pageshow', onPageShow)
      window.removeEventListener('pagehide', onPageHide)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [isOpen, containerId])
}
